import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import './ErrorBoundary.css';

interface Props {
  children: ReactNode;
  moduleName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error(`[ErrorBoundary:${this.props.moduleName || 'unknown'}]`, error, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div className="error-boundary-container">
        <div className="error-boundary-card">
          <AlertTriangle size={32} className="error-boundary-icon" />
          <h3 className="error-boundary-title">Something went wrong</h3>
          <p className="error-boundary-message">
            {this.props.moduleName ? `The ${this.props.moduleName} module` : 'This section'} encountered an unexpected error.
          </p>
          {this.state.error && (
            <details className="error-boundary-details">
              <summary>Error details</summary>
              <code>{this.state.error.message}</code>
            </details>
          )}
          <button className="error-boundary-btn" onClick={this.handleReset}>
            <RefreshCw size={14} />
            Try again
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
